// MA-623 — the ONE mastery reader shared by the skill tree and the recap.
//
// Before this file, `masteryScore` (the score-per-skill projection) lived
// only as a private function inside useSkillGraph.ts, and SessionRecap.tsx
// (plus all five session screens' `preMasteredRef` producers) counted
// "mastered" via a COMPLETELY DIFFERENT formula —
// `countDurablyMasteredSkills(deriveMasteryBand(...))` in progression.ts.
// The two readers diverged in both directions (see
// docs/build/specs/engine-truth-2026-09-08.md §E3): the recap could claim
// "+1 skill secured" while the tree still showed "Now", and vice versa.
//
// FOUNDER DECISION (2026-09-08, engine-truth §E3): ONE reader. The recap's
// "+N skill secured" tile now counts exactly what the skill tree counts —
// masteryTier(masteryScore(computeSkillMasteryState(...))) over the tree's
// SECURED_TREE_STATUSES — over the SAME merged cloud+local attempt rows the
// tree uses. This is a stricter bar than the old `fluent` band, so the tile
// fires less often. That is the accepted, honest outcome.
//
// This module is the ONLY place the score-per-skill projection is computed.
// Every consumer (useSkillGraph's tree nodes, SessionRecap's post-count, and
// every session screen's pre-session `preMasteredRef` capture) imports from
// here — no second formula, per the file's own §E3 "single-formula guard"
// pin (recapTreeSecuredParity.test.mjs).

import type { ChildAttempt } from "@/adaptation/types";
import type { MicroSkill } from "@/types/engine";
import { fetchAllAttemptsForChild } from "./attemptLogger";
import { loadLocalAttempts, mergeAttempts } from "./localAttemptStore";
import { masteryTier } from "./masteryTiers";
import { SECURED_TREE_STATUSES } from "./skillTreeStatus";
// MA-623 panel closure — the SAME manifest source useSkillGraph.ts iterates
// (SKILLS = microSkills.json). useSkillGraph only ever produces a node for an
// id IN this manifest; a retired/removed skill id that still has rows in
// child_attempts would otherwise inflate readTreeSecuredCount (it groups by
// whatever skillId appears in the attempt rows) relative to what the tree
// itself could ever count. Filtering to this set keeps `pre`/`post` counting
// exactly the tree's own skill population — no second, wider one.
import microSkills from "@/curriculum/data/manifests/microSkills.json" with { type: "json" };

const MANIFEST_SKILL_IDS = new Set((microSkills as MicroSkill[]).map((s) => s.id));

// masteryScore and computeSkillMasteryProjection live in masteryScore.ts (MA-874
// D-1101-25, a verbatim move) and are re-exported here unchanged; this module
// keeps the readers (countTreeSecuredSkills, countSecuredFromAttemptRows,
// readTreeSecuredCount) that use them.
import { masteryScore, computeSkillMasteryProjection } from "./masteryScore";
export { masteryScore, computeSkillMasteryProjection };

/** Count of skills the SKILL TREE renders as secured — i.e.
 *  masteryTier(mastery_score) is a member of SECURED_TREE_STATUSES
 *  ("secure" or "mastered"). THE recap must agree with the tree by
 *  construction: same projection, same banding fn, no second formula. */
export function countTreeSecuredSkills(
  nodes: readonly { mastery: { mastery_score: number } | null }[],
): number {
  return nodes.filter(
    (n) =>
      n.mastery != null &&
      (SECURED_TREE_STATUSES as readonly string[]).includes(masteryTier(n.mastery.mastery_score)),
  ).length;
}

/** Group a flat attempt list by skillId — the same grouping useSkillGraph's
 *  queryFn performs, extracted so the pre-session readers (which have no
 *  curriculum SKILLS list to iterate, unlike the tree) can share it. */
function groupBySkill(attempts: readonly ChildAttempt[]): Map<string, ChildAttempt[]> {
  const bySkill = new Map<string, ChildAttempt[]>();
  for (const a of attempts) {
    const arr = bySkill.get(a.skillId) ?? [];
    arr.push(a);
    bySkill.set(a.skillId, arr);
  }
  return bySkill;
}

/** Synchronous core of `readTreeSecuredCount`, over an already-merged flat
 *  attempt list — pulled out so the manifest-membership filter is directly
 *  testable without a network round-trip. Panel closure (2026-09-08,
 *  MA-623): filters to `MANIFEST_SKILL_IDS`, the SAME set `useSkillGraph`
 *  iterates (`SKILLS = microSkills.json`) — a retired/non-manifest skill id
 *  that still has rows in `child_attempts` can never produce a
 *  `useSkillGraph` node, so it must never inflate this count either. Without
 *  this filter, grouping by whatever `skillId` literally appears on an
 *  attempt row would count a retired id the tree could never show. */
export function countSecuredFromAttemptRows(attempts: readonly ChildAttempt[]): number {
  const bySkill = groupBySkill(attempts);
  const nodes = Array.from(bySkill.entries())
    .filter(([skillId]) => MANIFEST_SKILL_IDS.has(skillId))
    .map(([skillId, skillAttempts]) => ({
      mastery: { mastery_score: computeSkillMasteryProjection(skillId, skillAttempts).score },
    }));
  return countTreeSecuredSkills(nodes);
}

/** The ONE way to read "how many skills does the tree count as secured for
 *  this child right now" OUTSIDE the `useSkillGraph` hook — for the five
 *  session-mount `pre` producers (MathSession, ReadingSession, MixedSession,
 *  FoundationSession) which run in a plain effect, not through React Query.
 *  Merges cloud (childId) + local mirror (localChildId) attempts exactly as
 *  `useSkillGraph`'s queryFn does, so `pre` and the recap's post-count are
 *  drawn from the SAME row set and the SAME formula. */
export async function readTreeSecuredCount(
  childId: string | null | undefined,
  localChildId: string | null | undefined,
): Promise<number> {
  const cloud = childId ? await fetchAllAttemptsForChild(childId) : [];
  const all = mergeAttempts(cloud, loadLocalAttempts(localChildId, 500));
  return countSecuredFromAttemptRows(all);
}
